import React from 'react';

import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { 
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button, 
  createStyles, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  makeStyles, 
  Menu, 
  MenuItem, 
  Theme, 
  Typography, 
  useTheme
} from '@material-ui/core';

import useMediaQuery from '@material-ui/core/useMediaQuery';


import config from '../config';

import i18n from '../services/localization/i18n';
import Signout from './shared/Signout';

import MenuIcon from '@material-ui/icons/Menu';
import DashboardIcon from "@material-ui/icons/Dashboard";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordionDetails: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      alignItems: 'flex-start'
    },
    accordionSummary: {
      backgroundColor: "#393C6D",
      color: "white",
      display: "flex",
      flexDirection: "column",
      minHeight: theme.spacing(8),
      justifyContent: "center",
    },
    appBar: {
        backgroundColor: "#393C6D",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        minHeight: theme.spacing(9)
    },
    appBarBanner: {
      backgroundColor: "#393C6D",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      minHeight: theme.spacing(9),
      top: 50
    },
    avatar: {
      borderColor: "white",
      borderWidth: 3,
      borderStyle: "solid",
      height: theme.spacing(3),
      width: theme.spacing(3),
    },    
    button: {
        display: "flex",
        flexDirection: "row",
        fontSize: "16px",
        fontWeight: 300,
        justifyContent: "flex-start",
        lineHeight: "19px",
    },
    divider: {
      backgroundColor: "white",
      margin: theme.spacing(),
      width: "96%",
    },
    expandIcon: {
      color: 'white',
    },
    flag: {
      height: "auto", 
      width: 32,
      maxHeight: 24
    },
    menuButton: {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
    menuContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContext: "flex-end",
      marginRight: theme.spacing(4)
    },
    menuItem: {

    },
    mobileTitle: {
      marginLeft: 8,
      width: "100%"
    },
    proContainer: {
      display: "flex",
      flexDirection: "row",
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: theme.spacing(0.5),
        marginTop: theme.spacing(2),
      },
    },
    signInButton: {
      backgroundColor: "#f26d21",
      marginLeft: theme.spacing(2),
      padding: theme.spacing()
    },
  
    root: {
        justifyContent: "center"
    },
    title: {
        cursor: "pointer",
        flexGrow: 1,
        
    },
    toolbar: {
        alignItems: "center",
        display: "flex",
        width: '100%',
        justifyContent: "space-between",
        [theme.breakpoints.up("xl")]: {
          maxWidth: 1300,
        },
  
    }
  }),
);

declare type Languages = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pl' | 'sr-Latn' | 'sr-Cyrl';

const Header: React.FC = () => {
    const classes = useStyles();
    const theme = useTheme();

    const mobileLayout = useMediaQuery(theme.breakpoints.down('xs'));

    const getDefaultLanguage = () : Languages => {
      let dlang = localStorage.getItem("meet-i18n");

      if (!dlang) {
        return "en";
      }
      
      dlang = dlang.toLowerCase(); 

      if (dlang.indexOf("en") === 0) {
        return "en";
      }

      if (dlang.indexOf("es") === 0) {
        return "es";
      }

      if (dlang.indexOf("fr") === 0) {
        return "fr";
      }

      
      if (dlang.indexOf("de") === 0) {
        return "de";
      }
         
      if (dlang.indexOf("it") === 0) {
        return "it";
      }
      
      if (dlang.indexOf("pl") === 0) {
        return "pl";
      }

      if (dlang.indexOf("sr-cyrl") === 0) {
        return "sr-Cyrl";
      }
      
      if (dlang.indexOf("sr") === 0) {
        return "sr-Latn";
      }

      console.warn("[www]::Header- locale fallback-> en");
      return  "en";
    }

    const user = config.me && config.me.isAuthenticated && !config.me.isGuest;

    const [anchorEl, setAnchorEl] = React.useState<EventTarget & HTMLButtonElement | null>(null);

    const showLanguageSelector = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    };
  
    const closeLanguageSelector = () => {
      setAnchorEl(null);
    };

    const changeLanguage = (language: Languages ) => {
        i18n.changeLanguage(language);
        window.location.reload();
    };

    const getIconUrl = (language: Languages ) => {
      switch (language) {
        case "en":
          return `${config.meet.hosts.cdn}/assets/flags/flag_uk.svg`;
        case "fr":
          return `${config.meet.hosts.cdn}/assets/flags/flag_france.svg`;
        case "de":
          return `${config.meet.hosts.cdn}/assets/flags/flag_germany.svg`;
        case "es":
          return `${config.meet.hosts.cdn}/assets/flags/flag_spain.svg`;
        case "it":
          return `${config.meet.hosts.cdn}/assets/flags/flag_italy.svg`;
        case "pl":
            return `${config.meet.hosts.cdn}/assets/flags/flag_poland.svg`;
        case "sr-Latn":
          return `${config.meet.hosts.cdn}/assets/flags/flag_serbia.svg`;
        case "sr-Cyrl":
          return `${config.meet.hosts.cdn}/assets/flags/flag_serbia.svg`;
        default: 
          console.error("[www][Header]::getIconUrl-unsupported language:", language);
          break;        
      }
    }

    const renderLanguagePicker = () => {
      const lang = getDefaultLanguage();
      return <>
        <Button variant="text" aria-controls="simple-menu" aria-haspopup="true" onClick={showLanguageSelector}>
            <img  src={getIconUrl(lang)} 
                  className={classes.flag}
                  alt={"Change language"} />
            
        </Button>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted={true}
          open={Boolean(anchorEl)}
          onClose={closeLanguageSelector}
        >

        <MenuItem onClick={() => changeLanguage("en")}>
          <ListItemIcon>
            <img  src={getIconUrl("en")} 
                  className={classes.flag}
                  alt={ i18n.t("language_en_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_en_title") }
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage("es")}>
          <ListItemIcon>
            <img  src={getIconUrl("es")} 
                  className={classes.flag}
                  alt={ i18n.t("language_es_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_es_title") }
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage("fr")}>
          <ListItemIcon>
            <img  src={getIconUrl("fr")} 
                  className={classes.flag}
                  alt={ i18n.t("language_fr_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_fr_title") }
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage("de")}>
          <ListItemIcon>
            <img  src={getIconUrl("de")} 
                  className={classes.flag}
                  alt={ i18n.t("language_de_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_de_title") }
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage("it")}>
          <ListItemIcon>
            <img  src={getIconUrl("it")} 
                  className={classes.flag}
                  alt={ i18n.t("language_it_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_it_title") }
          </Typography>
        </MenuItem>        
        <MenuItem onClick={() => changeLanguage("pl")}>
          <ListItemIcon>
            <img  src={getIconUrl("pl")} 
                  className={classes.flag}
                  alt={ i18n.t("language_pl_title") } />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_pl_title") }
          </Typography>
        </MenuItem>        
        <MenuItem onClick={() => changeLanguage("sr-Latn")}>
          <ListItemIcon>
            <img  src={getIconUrl("sr-Latn")} 
                  className={classes.flag}
                  alt={ i18n.t("language_srLatn_title") }  />
          </ListItemIcon>
          <Typography variant="inherit">
            { i18n.t("language_srLatn_title") } 
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => changeLanguage("sr-Cyrl")} >
            <ListItemIcon>
              <img  src={getIconUrl("sr-Cyrl")} 
                    className={classes.flag}
                    alt={ i18n.t("language_srCyrl_title") }  />
            </ListItemIcon>
            <Typography variant="inherit">
              { i18n.t("language_srCyrl_title") }
            </Typography>
          </MenuItem>
        </Menu>
      </>
    }

    const toolbarMenu = () => {
      return <AppBar position="fixed" color="primary" className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <Typography variant="h6" onClick={() => window.location.hash=""}>
              {  i18n.t('header_title') }
            </Typography>
      {/*               
            <Button className={classes.button} variant="text" color="inherit">Recruiters</Button>
            <Button className={classes.button} variant="text" color="inherit">Interviewers</Button>
            <Button className={classes.button} variant="text" color="inherit">Pricing</Button>
      */}                

          <div className={classes.menuContainer}>
      {/*
            <Button className={classes.button} 
                    href="#/plans"
                    variant="text" 
                    color="inherit">
                    Pricing
            </Button>
      */}
            <Button className={classes.button} 
                    variant="text" color="inherit" 
                    href="https://github.com/vivani-doo/meet-api-help"
                    target="_blank" 
                    rel="noopener noreferrer">
                      {  i18n.t('header_menu_api') }
            </Button>
            <Button className={classes.button} 
                    variant="text" color="inherit" 
                    href="https://building.meet.rs?src=www"
                    target="_blank" 
                    rel="noopener noreferrer">
                      Blog
            </Button>
            {/*
              <Button className={classes.button} 
                      variant="text" 
                      color="inherit"
                      href="#/docs/help">
                { i18n.t("help_menu_text") }
              </Button>
          */}

            { renderLanguagePicker() }

            <div className = {classes.proContainer}>
              {!user && 
                <Button variant="outlined" 
                        href="/pro"
                        color="inherit">
                        
                          {i18n.t('header_signin_text') }
                </Button>
              }

              { user && <Signout />}

            </div>
          </div>
        </Toolbar>
      </AppBar>
    }

    const getInitials = () => {

      if (!config.me) {
          return "N/A";
      }

      let initials = config.me.firstName.substr(0, 1);
      const lastName = config.me.familyName!.substr(0, 1);
      if (lastName !== "(") {
          initials += lastName;
      }
      return initials;
    }

    const loggedInMenu = () => {
        return <>
          <ListItem onClick={() => window.location.href = "/pro"} className={classes!.menuItem} >
              <ListItemIcon>
                  <DashboardIcon style={{
                    color: "white",
                    height: 32,
                    width: 32
                  }} />
              </ListItemIcon>
              <ListItemText primary={ i18n.t('header_dashboard_text') } />
          </ListItem>
          <ListItem onClick={() => window.location.href = "/pro"} 
                    className={classes!.menuItem} >
              <ListItemIcon>
                <Avatar className={classes!.avatar} 
                        src={config.me!.avatarUrl} 
                        title={`${config.me!.firstName} ${config.me!.familyName} (${config.me!.email})`}
                        style={{
                            backgroundColor: `${config.me!.color}`
                        }}>
                            
                        <Typography variant="button">
                            {getInitials()}
                        </Typography>
                    
                </Avatar>
              </ListItemIcon>                            
              <ListItemText primary={ i18n.t('signout_menu_text') } />
          </ListItem>
        </>
    }

    const hamburgerMenu = () => {
      return  <Accordion square={true} classes={{
        root: classes.accordionSummary
      }}>
        <AccordionSummary 
            expandIcon={<MenuIcon className={ classes.expandIcon } />} 
            aria-controls="menu-content">

            <Typography variant="h5" className={classes.mobileTitle}
                        onClick={() => window.location.hash=""}>
              {  i18n.t('header_title_short') }
            </Typography>
                          
            { renderLanguagePicker() }
        </AccordionSummary>
        <AccordionDetails >
          <div className={classes.accordionDetails}>
            <Divider orientation='horizontal' className={classes.divider}   />

            <List style={{
              width: '100%'
            }}>
                <ListItem button={true} 
                          onClick={() => window.location.href = "https://github.com/vivani-doo/meet-api-help"} 
                          className={classes!.menuItem} >
                    <ListItemText primary={ i18n.t('header_menu_api') } />
                </ListItem>
                <ListItem onClick={() => window.location.hash = "/docs/help"} 
                          className={classes!.menuItem} >
                    <ListItemText primary={ i18n.t('help_menu_text') } />
                </ListItem>
                
                { user &&  <Divider orientation='horizontal' className={classes.divider} /> }
                { user && loggedInMenu() }

                {!user && 
                  <Button variant="outlined" 
                          href="/pro"
                          fullWidth={true}
                          color="inherit" 
                          style={{
                            marginTop: 16
                          }}>
                          
                            {i18n.t('header_signin_text') }
                  </Button>
                }
            </List>
          </div>
        </AccordionDetails>
      </Accordion>
    }

    return (
        <header className={classes.root}>
          { !mobileLayout && toolbarMenu() }
          { mobileLayout && hamburgerMenu() }
        </header>
    );

  }
  
  
  export default Header;